"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import Button from "../../../../../components/Button";
import FormInput from "../../../../../components/FormInput";
import Checkbox from "../../../../../components/Checkbox";
import { Check } from "lucide-react";

import { useConsultation } from "../../../../../context/ConsultationContext";
import { validateDateOfBirth, validateFamilyPlanningConsentForm } from "../../utils/validation";

import styles from "./css/InjectionAdministration.module.css";

const initialForm = { patientFullName: "", patientIdOrPassport: "", patientDob: "", patientCellphone: "", patientAddress: "", injectionType: "", dateGiven: "", batch: "", nextDueDate: "", expiry: "", popiaConsent: false, patientSignature: "", patientSignatureDate: "", pharmacistSignature: "", pharmacistSignatureDate: "", nurseSignature: "", nurseSignatureDate: "" };

export default function FamilyPlanning() {
  const navigate = useNavigate();
  const location = useLocation();
  const { consultationData, setIsInConsultation, getStepDraft, setStepDraft } = useConsultation();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayStr = useMemo(() => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(today.getDate())}-${pad(today.getMonth() + 1)}-${today.getFullYear()}`;
  }, [today]);

  const patient = consultationData?.patient || {};
  const patientName = useMemo(() => {
    const first = String(patient?.firstname || "").trim();
    const last = String(patient?.surname || "").trim();
    const full = `${first} ${last}`.trim();
    return full;
  }, [patient?.firstname, patient?.surname]);

  const [formData, setFormData] = useState(() => ({
    ...initialForm, patientFullName: patientName || initialForm.patientFullName, patientSignatureDate: todayStr,pharmacistSignatureDate: todayStr, nurseSignatureDate: todayStr,
  }));

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }; delete newErrors[name]; return newErrors;
      });
    }

    if (name === "patientDob") {
      const nextValue = String(value || "").trim();
      if (!nextValue) {
        setValidationErrors((prev) => {
          if (!prev.patientDob) return prev;const next = { ...prev }; delete next.patientDob;
          return next;
        });
        return;
      }

      const dobValidation = validateDateOfBirth(nextValue);
      setValidationErrors((prev) => {
        if (!dobValidation.isValid) {
          return { ...prev, patientDob: dobValidation.error };
        }
        if (!prev.patientDob) return prev;
        const next = { ...prev };
        delete next.patientDob;
        return next;
      });
    }
  };

  const focusFirstError = (errors) => {
    const fieldOrder = [
      "patientFullName","patientIdOrPassport","patientDob","patientCellphone","patientAddress","injectionType","dateGiven","batch","nextDueDate","expiry","popiaConsent","patientSignature","pharmacistSignature","nurseSignature",
    ];

    const field = fieldOrder.find((f) => errors?.[f]);
    if (!field) return;

    const el = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (el?.focus) el.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // Validate form
    const errors = validateFamilyPlanningConsentForm(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setTimeout(() => focusFirstError(errors), 0);
      return;
    }

    setSaveError("");
    setSaving(true);

    try {
      const payload = {
        ...formData,
        appointmentId: consultationData?.appointmentId ?? null,patientId: consultationData?.patientId ?? null,
      };

      // Save to consultation draft context (for DB save on Invoice Finalize)
      const existing = getStepDraft?.("consentForms", {}) || {};
      setStepDraft?.("consentForms", {
        ...existing,
        family_planning: { answers: payload, patientSigned: !!formData.patientSignature, patientSignedAt: formData.patientSignatureDate ?? null, practitionerSigned: !!(formData.pharmacistSignature || formData.nurseSignature), practitionerSignedAt: formData.pharmacistSignatureDate ?? formData.nurseSignatureDate ?? null },
      });

      try {
        sessionStorage.setItem("clinic.consent.familyPlanning", JSON.stringify(payload));
      } catch {}

      const continueTo = location?.state?.continueTo;
      setIsInConsultation?.(true);
      navigate(typeof continueTo === "string" && continueTo.trim() ? continueTo : "/customer/clinic/patient-overview");
    } catch (err) {
      setSaveError(err?.message || "Failed to save consent form.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <form className={styles.formContainer} onSubmit={handleSubmit}>
            <header className={styles.docHeader}>
              <div className={styles.docHeaderTop}>
                <div className={styles.pharmacyName}>Durban North Pharmacy</div>
                <div className={styles.pharmacyContact}>35 Umhlanga Rocks Drive | Tel: 031 563 7577</div>
              </div>
              <div className={styles.purpleRule} />
              <h1 className={styles.docTitle}>
                Family Planning Services Informed Consent- Injectable Progesterone Contraception
              </h1>
            </header>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>1. Patient Details</h3>
              <div className={styles.grid2}>
                <div className={styles.inputGroup}>
                  <label htmlFor="patientFullName">Full Name</label>
                  <input
                    id="patientFullName"
                    name="patientFullName"
                    value={formData.patientFullName}
                    onChange={handleInputChange}
                    placeholder="Full name"
                    autoComplete="name"
                    className={validationErrors.patientFullName ? styles.inputError : ""}
                  />
                  {validationErrors.patientFullName && (
                    <div className={styles.errorMessage}>{validationErrors.patientFullName}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="patientIdOrPassport">ID Number / Passport</label>
                  <input
                    id="patientIdOrPassport"
                    name="patientIdOrPassport"
                    value={formData.patientIdOrPassport}
                    onChange={handleInputChange}
                    placeholder="ID number / passport"
                    className={validationErrors.patientIdOrPassport ? styles.inputError : ""}
                  />
                  {validationErrors.patientIdOrPassport && (
                    <div className={styles.errorMessage}>{validationErrors.patientIdOrPassport}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="patientDob">Date of Birth</label>
                  <input
                    id="patientDob"
                    name="patientDob"
                    value={formData.patientDob}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    className={validationErrors.patientDob ? styles.inputError : ""}
                  />
                  {validationErrors.patientDob && (
                    <div className={styles.errorMessage}>{validationErrors.patientDob}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="patientCellphone">Cellphone</label>
                  <input
                    id="patientCellphone"
                    name="patientCellphone"
                    value={formData.patientCellphone}
                    onChange={handleInputChange}
                    placeholder="Cellphone"
                    autoComplete="tel"
                    className={validationErrors.patientCellphone ? styles.inputError : ""}
                  />
                  {validationErrors.patientCellphone && (
                    <div className={styles.errorMessage}>{validationErrors.patientCellphone}</div>
                  )}
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="patientAddress">Address</label>
                <textarea
                  id="patientAddress"
                  name="patientAddress"
                  value={formData.patientAddress}
                  onChange={handleInputChange}
                  placeholder="Address"
                  className={`${styles.textarea} ${validationErrors.patientAddress ? styles.inputError : ""}`}
                />
                {validationErrors.patientAddress && (
                  <div className={styles.errorMessage}>{validationErrors.patientAddress}</div>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="injectionType">Injection Type</label>
                <input
                  id="injectionType"
                  name="injectionType"
                  value={formData.injectionType}
                  onChange={handleInputChange}
                  className={validationErrors.injectionType ? styles.inputError : ""}
                />
                {validationErrors.injectionType && (
                  <div className={styles.errorMessage}>{validationErrors.injectionType}</div>
                )}
              </div>

              <div className={styles.grid2}>
                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date Given"
                    type="text"
                    name="dateGiven"
                    value={formData.dateGiven}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    inputClassName={validationErrors.dateGiven ? styles.inputError : ""}
                  />
                  {validationErrors.dateGiven && (
                    <div className={styles.errorMessage}>{validationErrors.dateGiven}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="batch">Batch</label>
                  <input
                    id="batch"
                    name="batch"
                    value={formData.batch}
                    onChange={handleInputChange}
                    placeholder="Batch"
                    className={validationErrors.batch ? styles.inputError : ""}
                  />
                  {validationErrors.batch && (
                    <div className={styles.errorMessage}>{validationErrors.batch}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Next Due Date"
                    type="text"
                    name="nextDueDate"
                    value={formData.nextDueDate}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    inputClassName={validationErrors.nextDueDate ? styles.inputError : ""}
                  />
                  {validationErrors.nextDueDate && (
                    <div className={styles.errorMessage}>{validationErrors.nextDueDate}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Expiry"
                    type="text"
                    name="expiry"
                    value={formData.expiry}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    inputClassName={validationErrors.expiry ? styles.inputError : ""}
                  />
                  {validationErrors.expiry && (
                    <div className={styles.errorMessage}>{validationErrors.expiry}</div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>Purpose</h3>
              <div className={styles.infoBlock}>
                <p className={styles.statement}>
                  This injection prevents pregnancy by suppressing ovulation, thickening cervical mucus, and thinning uterine lining. Effectiveness
                  &gt;99% when on schedule
                </p>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>Possible Side Effects</h3>
              <div className={styles.infoBlock}>
                <p className={styles.statement}>
                  Irregular bleeding or amenorrhea, weight gain, mood changes, headache, breast tenderness, acne, delayed return to fertility (6–12
                  months), possible temporary reduction in bone mineral density.
                </p>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>Risks and Warnings</h3>
              <div className={styles.infoBlock}>
                <p className={styles.statement}>
                  Seek urgent care if you experience severe headache, visual changes, chest pain, leg swelling, severe abdominal pain, or signs of
                  pregnancy.
                </p>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>6. Bone Density Advisory</h3>
              <div className={styles.infoBlock}>
                <p className={styles.statement}>
                  Long-term use (&gt;2 years) may reduce bone mineral density. Risk is higher in adolescents and patients with osteoporosis risk factors.
                  Lifestyle advice: adequate calcium intake, vitamin D, weight-bearing exercise.
                </p>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>7. POPIA Consent</h3>
              <div className={styles.infoBlock}>
                <p className={styles.statement}>
                  I consent to Durban North Pharmacy collecting and processing my personal and health information for the purpose of providing
                  healthcare services, in accordance with the Protection of Personal Information Act (POPIA).
                </p>
              </div>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id="popiaConsent"
                  label="I consent to the use of my personal information (POPIA)."
                  checked={formData.popiaConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, popiaConsent: e.target.checked }))}
                />
              </div>
              {validationErrors.popiaConsent && (
                <div className={styles.errorMessage}>{validationErrors.popiaConsent}</div>
              )}
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>8. Declaration</h3>
              <div className={styles.infoBlock}>
                <p className={styles.statement}>I confirm that information was explained to me and I voluntarily consent.</p>
              </div>

              <div className={styles.grid2}>
                <div className={styles.inputGroup}>
                  <label htmlFor="patientSignature">Patient Signature</label>
                  <input
                    id="patientSignature"
                    name="patientSignature"
                    value={formData.patientSignature}
                    onChange={handleInputChange}
                    placeholder="Type name as signature"
                    className={validationErrors.patientSignature ? styles.inputError : ""}
                  />
                  {validationErrors.patientSignature && (
                    <div className={styles.errorMessage}>{validationErrors.patientSignature}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date"
                    type="text"
                    name="patientSignatureDate"
                    value={formData.patientSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="pharmacistSignature">Pharmacist Signature</label>
                  <input
                    id="pharmacistSignature"
                    name="pharmacistSignature"
                    value={formData.pharmacistSignature}
                    onChange={handleInputChange}
                    placeholder="Type name as signature"
                    className={validationErrors.practitionerSignature ? styles.inputError : ""}
                  />
                  {validationErrors.practitionerSignature && (
                    <div className={styles.errorMessage}>{validationErrors.practitionerSignature}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date"
                    type="text"
                    name="pharmacistSignatureDate"
                    value={formData.pharmacistSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="nurseSignature">Nurse Signature</label>
                  <input
                    id="nurseSignature"
                    name="nurseSignature"
                    value={formData.nurseSignature}
                    onChange={handleInputChange}
                    placeholder="Type name as signature"
                    className={validationErrors.practitionerSignature ? styles.inputError : ""}
                  />
                  {validationErrors.practitionerSignature && (
                    <div className={styles.errorMessage}>{validationErrors.practitionerSignature}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date"
                    type="text"
                    name="nurseSignatureDate"
                    value={formData.nurseSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                  {validationErrors.practitionerSignatureDate && (
                    <div className={styles.errorMessage}>{validationErrors.practitionerSignatureDate}</div>
                  )}
                </div>
              </div>
            </div>

            {saveError && <div className={styles.inlineError}>{saveError}</div>}

            <div className={styles.completeContainer}>
              <Button variant="primary" className={styles.completeButton} disabled={saving} type="submit">
                {saving ? (
                  "Completing..."
                ) : (
                  <>
                    Complete <Check size={22} />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}